import type { SettingsSample } from './settingsSample';

/**
 * Who can log time on the project, and how a change moves that number
 * (docs/briefs/settings-tab.md §4.2, decision 4). Pure, so every count the
 * page shows is assertable — the brief's "14" and "196" are examples, never
 * typed in.
 */

/** The five levels of §4.2, widest first. */
export const LOG_TIME_SCOPES = [
  'all',
  'legalEntity',
  'department',
  'projectTeam',
  'allocatedOnly',
] as const;
export type LogTimeScope = (typeof LOG_TIME_SCOPES)[number];

export type LogTimeSetting = {
  scope: LogTimeScope;
  /** The Owners values the legal-entity and department levels read. */
  legalEntityId: string;
  departmentId: string;
};

/** A department and every department under it, walked iteratively. */
export const departmentWithDescendants = (
  sample: Pick<SettingsSample, 'departments'>,
  departmentId: string,
): Set<string> => {
  const found = new Set<string>([departmentId]);
  const queue = [departmentId];
  while (queue.length > 0) {
    const parentId = queue.shift()!;
    for (const department of sample.departments) {
      if (department.parentId === parentId && !found.has(department.id)) {
        found.add(department.id);
        queue.push(department.id);
      }
    }
  }
  return found;
};

/**
 * Everyone the level covers ∪ everyone allocated to a task ("People directly
 * allocated to a task can always log time on it"), as unique employee ids.
 */
export const peopleWhoCanLogTime = (
  sample: SettingsSample,
  setting: LogTimeSetting,
): Set<string> => {
  const people = new Set<string>(Object.keys(sample.allocations));
  const { employees } = sample;

  switch (setting.scope) {
    case 'all':
      for (const employee of employees) people.add(employee.id);
      break;
    case 'legalEntity':
      for (const employee of employees) {
        if (employee.legalEntityId === setting.legalEntityId) people.add(employee.id);
      }
      break;
    case 'department': {
      const departments = departmentWithDescendants(sample, setting.departmentId);
      for (const employee of employees) {
        if (departments.has(employee.departmentId)) people.add(employee.id);
      }
      break;
    }
    case 'projectTeam':
      for (const id of sample.projectTeam) people.add(id);
      break;
    case 'allocatedOnly':
      break;
  }
  return people;
};

export type ReachChange = {
  before: number;
  after: number;
  gaining: string[];
  losing: string[];
  keeping: string[];
};

/**
 * Before → after, split three ways. By construction the parts add up:
 * keeping + gaining = after, keeping + losing = before (§4.2).
 */
export const compareReach = (before: Set<string>, after: Set<string>): ReachChange => {
  const gaining: string[] = [];
  const losing: string[] = [];
  const keeping: string[] = [];
  for (const id of after) (before.has(id) ? keeping : gaining).push(id);
  for (const id of before) if (!after.has(id)) losing.push(id);
  return { before: before.size, after: after.size, gaining, losing, keeping };
};

/** Why one person can log time — the people modal's "Why" column. */
export type ReachReason =
  | { kind: 'allocated'; taskCount: number }
  | { kind: 'team' }
  | { kind: 'department'; departmentName: string }
  | { kind: 'legalEntity'; legalEntityName: string }
  | { kind: 'all' };

/**
 * The most specific reason, or `null` when the setting does not reach them.
 * Being allocated wins over every level, since it holds whatever the level.
 */
export const reachReason = (
  sample: SettingsSample,
  setting: LogTimeSetting,
  employeeId: string,
): ReachReason | null => {
  const tasks = sample.allocations[employeeId];
  if (tasks !== undefined) return { kind: 'allocated', taskCount: tasks.length };
  const employee = sample.employees.find((candidate) => candidate.id === employeeId);
  if (!employee) return null;

  switch (setting.scope) {
    case 'all':
      return { kind: 'all' };
    case 'legalEntity': {
      if (employee.legalEntityId !== setting.legalEntityId) return null;
      const entity = sample.legalEntities.find((e) => e.id === employee.legalEntityId);
      return { kind: 'legalEntity', legalEntityName: entity?.name ?? '' };
    }
    case 'department': {
      if (!departmentWithDescendants(sample, setting.departmentId).has(employee.departmentId)) {
        return null;
      }
      const department = sample.departments.find((d) => d.id === employee.departmentId);
      return { kind: 'department', departmentName: department?.name ?? '' };
    }
    case 'projectTeam':
      return sample.projectTeam.includes(employeeId) ? { kind: 'team' } : null;
    case 'allocatedOnly':
      return null;
  }
};

export type ReachChangeKind = 'gaining' | 'losing' | 'keeping';

export type ReachPerson = {
  id: string;
  name: string;
  departmentName: string;
  change: ReachChangeKind;
  /** After's reason for anyone who still can; before's for someone losing it. */
  reason: ReachReason;
};

/** Everyone before ∪ after, for the people modal — sorted by name. */
export const reachPeople = (
  sample: SettingsSample,
  before: LogTimeSetting,
  after: LogTimeSetting,
  change: ReachChange,
): ReachPerson[] => {
  const departmentName = new Map(sample.departments.map((d) => [d.id, d.name]));
  const byId = new Map(sample.employees.map((employee) => [employee.id, employee]));
  const people: ReachPerson[] = [];
  const add = (ids: string[], kind: ReachChangeKind, setting: LogTimeSetting) => {
    for (const id of ids) {
      const employee = byId.get(id);
      const reason = reachReason(sample, setting, id);
      if (!employee || !reason) continue;
      people.push({
        id,
        name: employee.name,
        departmentName: departmentName.get(employee.departmentId) ?? '',
        change: kind,
        reason,
      });
    }
  };
  add(change.gaining, 'gaining', after);
  add(change.losing, 'losing', before);
  add(change.keeping, 'keeping', after);
  return people.sort((a, b) => a.name.localeCompare(b.name, 'da'));
};
